import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatMoney } from "@/lib/format";
import { SOURCES, TIME_SLOTS } from "@/lib/bookingConstants";
import { commissionRuleText, COMMISSION_TYPES } from "@/lib/partnerConstants";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FieldSelect } from "@/components/ui/field-select";
import { Loader2, AlertTriangle, Car, User, Phone, Percent, Tag, X, Check, Handshake } from "lucide-react";

const label = "mb-1.5 block text-sm font-medium text-slate-700";
const input = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
const section = "text-xs font-semibold uppercase tracking-wider text-slate-500";

export default function BookingFormDrawer({ open, onOpenChange, booking, prefill, onSaved }) {
  const { orgId, can } = useAuth();
  const qc = useQueryClient();
  const isEdit = !!booking;

  const { data: opts } = useQuery({ queryKey: ["booking-options", orgId], queryFn: async () => (await api.get("/api/catalogue/booking-options", { params: { organization_id: orgId } })).data, enabled: open });
  const { data: emps } = useQuery({ queryKey: ["employees", orgId], queryFn: async () => (await api.get("/api/employees", { params: { organization_id: orgId } })).data, enabled: open });

  const [cust, setCust] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [activityId, setActivityId] = useState("");
  const [formulaId, setFormulaId] = useState("");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("09:00");
  const [pax, setPax] = useState(2);
  const [extras, setExtras] = useState({});      // extraId -> quantity
  const [discOpen, setDiscOpen] = useState(false);
  const [discType, setDiscType] = useState("PERCENTAGE");
  const [discValue, setDiscValue] = useState("");
  const [discReason, setDiscReason] = useState("");
  const [source, setSource] = useState("WALK_IN");
  const [vehicleIds, setVehicleIds] = useState([]);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [notes, setNotes] = useState("");
  const [ovOpen, setOvOpen] = useState(false);
  const [ovVal, setOvVal] = useState("");
  const [ovReason, setOvReason] = useState("");
  const [conflicts, setConflicts] = useState([]);
  const [partnerId, setPartnerId] = useState("NONE");
  const [commOvOpen, setCommOvOpen] = useState(false);
  const [commType, setCommType] = useState("PERCENTAGE");
  const [commValue, setCommValue] = useState("");
  const [commReason, setCommReason] = useState("");

  const { data: partnersData } = useQuery({ queryKey: ["partners-active", orgId], queryFn: async () => (await api.get("/api/partners", { params: { organization_id: orgId, status: "ACTIVE" } })).data, enabled: open && can("partners.view") });

  const { data: vehs } = useQuery({ queryKey: ["vehicles", orgId, formulaId], queryFn: async () => (await api.get("/api/vehicles", { params: { organization_id: orgId, formula_id: formulaId || undefined } })).data, enabled: open });

  useEffect(() => {
    if (!open) return;
    if (booking) {
      setCust({ first_name: "", last_name: "", phone: booking.customer_phone || "", email: "" });
      setFoundCustomer({ id: booking.customer_id, name: booking.customer_name, phone: booking.customer_phone });
      setActivityId(booking.activity_id); setFormulaId(booking.formula_id); setDate(booking.booking_date);
      setStart(booking.start_time); setPax(booking.participants_count); setSource(booking.source);
      setVehicleIds(booking.assigned_vehicle_ids || []); setEmployeeIds(booking.assigned_employee_ids || []);
      setNotes(booking.internal_notes || "");
      const ex = {}; (booking.booking_items || []).filter((i) => i.type === "EXTRA").forEach((i) => { ex[i.ref_id] = i.quantity; });
      setExtras(ex);
      if (booking.discount_type) { setDiscOpen(true); setDiscType(booking.discount_type); setDiscValue(String(booking.discount_value ?? "")); setDiscReason(booking.discount_reason || ""); }
      if (booking.price_override_cents != null) { setOvOpen(true); setOvVal(String(booking.price_override_cents / 100)); setOvReason(booking.price_override_reason || ""); }
      setPartnerId(booking.partner_id || "NONE");
      if (booking.commission_override) {
        setCommOvOpen(true); setCommType(booking.commission_type_snapshot || "PERCENTAGE");
        setCommValue(String(booking.commission_type_snapshot === "FIXED_AMOUNT" ? (booking.commission_value_snapshot || 0) / 100 : (booking.commission_value_snapshot ?? "")));
        setCommReason(booking.commission_override_reason || "");
      } else { setCommOvOpen(false); setCommValue(""); setCommReason(""); }
    } else {
      setCust({ first_name: "", last_name: "", phone: "", email: "" }); setFoundCustomer(null);
      setActivityId(""); setFormulaId(""); setDate(prefill?.date || new Date().toISOString().slice(0, 10));
      setStart(prefill?.start_time || "09:00"); setPax(2); setExtras({});
      setDiscOpen(false); setDiscValue(""); setDiscReason(""); setSource("WALK_IN");
      setVehicleIds([]); setEmployeeIds([]); setNotes("");
      setOvOpen(false); setOvVal(""); setOvReason("");
      setPartnerId("NONE"); setCommOvOpen(false); setCommValue(""); setCommReason("");
    }
    setConflicts([]);
  }, [open, booking, prefill]);

  // Existing customer detection by phone
  useEffect(() => {
    const p = cust.phone.trim();
    if (isEdit || p.length < 6) { if (!isEdit) setFoundCustomer(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/api/customers/search", { params: { q: p, organization_id: orgId } });
        const m = (data.customers || []).find((c) => (c.phone || "").replace(/\s/g, "").includes(p.replace(/\s/g, "")));
        setFoundCustomer(m || null);
      } catch { setFoundCustomer(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [cust.phone, orgId, isEdit]);

  const activities = opts?.activities || [];
  const formulas = useMemo(() => (opts?.formulas || []).filter((f) => f.activity_id === activityId), [opts, activityId]);
  const formula = (opts?.formulas || []).find((f) => f.id === formulaId);
  const currency = opts?.currency || "MAD";
  const compatExtras = useMemo(() => (opts?.extras || []).filter((e) => {
    const aa = e.applicable_activity_ids || []; const af = e.applicable_formula_ids || [];
    return (!aa.length || aa.includes(activityId)) && (!af.length || af.includes(formulaId));
  }), [opts, activityId, formulaId]);

  const endTime = useMemo(() => {
    if (!formula) return "";
    const [h, m] = start.split(":").map(Number);
    const t = (h * 60 + m + formula.duration_minutes) % 1440;
    return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  }, [formula, start]);

  // Live price summary (backend is authoritative)
  const summary = useMemo(() => {
    if (!formula) return null;
    const items = [];
    const fq = formula.pricing_type === "FIXED" ? 1 : Math.max(1, pax);
    const fu = formula.pricing_type === "FIXED" ? formula.fixed_price : formula.unit_price;
    items.push({ name: formula.name, qty: fq, unit: fu, total: fu * fq });
    Object.entries(extras).forEach(([id, q]) => {
      const e = (opts?.extras || []).find((x) => x.id === id); if (!e) return;
      const qty = e.quantity_mode === "ALL_PARTICIPANTS" ? Math.max(1, pax) : e.quantity_mode === "FIXED_ONE" ? 1 : Math.max(1, q);
      const unit = e.pricing_type === "FIXED" ? e.fixed_price : e.unit_price;
      items.push({ name: e.name, qty, unit, total: unit * qty });
    });
    const subtotal = items.reduce((s, i) => s + i.total, 0);
    let damount = 0;
    if (discOpen && discValue !== "") {
      if (discType === "PERCENTAGE") damount = Math.round(subtotal * Math.max(0, Math.min(100, parseFloat(discValue))) / 100);
      else damount = Math.min(Math.round(parseFloat(discValue) * 100), subtotal);
    }
    return { items, subtotal, damount, final: subtotal - damount };
  }, [formula, pax, extras, discOpen, discType, discValue, opts]);

  const selectedPartner = (partnersData?.partners || []).find((p) => p.id === partnerId);
  const commissionPreview = useMemo(() => {
    if (partnerId === "NONE" || !selectedPartner || !summary) return null;
    const base = (ovOpen && ovVal !== "") ? (Math.round(parseFloat(ovVal) * 100) || 0) : summary.final;
    let type, amount;
    if (commOvOpen) {
      type = commType;
      const v = parseFloat(commValue) || 0;
      amount = type === "PERCENTAGE" ? Math.round(base * v / 100) : Math.round(v * 100);
    } else {
      type = selectedPartner.default_commission_type;
      const v = selectedPartner.default_commission_value;
      if (!type || v == null) return { base, type: null, amount: 0, net: base };
      amount = type === "PERCENTAGE" ? Math.round(base * v / 100) : v;
    }
    amount = Math.max(0, amount);
    return { base, type, amount, net: base - amount };
  }, [partnerId, selectedPartner, summary, ovOpen, ovVal, commOvOpen, commType, commValue]);

  const save = useMutation({
    mutationFn: async (override) => {
      const body = {
        organization_id: orgId, formula_id: formulaId, booking_date: date, start_time: start,
        participants_count: Number(pax), source, assigned_vehicle_ids: vehicleIds,
        assigned_employee_ids: employeeIds, internal_notes: notes || null, override_conflict: !!override,
        extras: Object.entries(extras).map(([extra_id, quantity]) => ({ extra_id, quantity: Number(quantity) || 1 })),
        discount_type: discOpen && discValue !== "" ? discType : null,
        discount_value: discOpen && discValue !== "" ? parseFloat(discValue) : null,
        discount_reason: discOpen && discValue !== "" ? (discReason || null) : null,
        price_override_cents: ovOpen && ovVal !== "" ? Math.round(parseFloat(ovVal) * 100) : null,
        price_override_reason: ovOpen && ovVal !== "" ? (ovReason || null) : null,
        partner_id: partnerId === "NONE" ? "" : partnerId,
        commission_type: (partnerId !== "NONE" && commOvOpen) ? commType : null,
        commission_value: (partnerId !== "NONE" && commOvOpen && commValue !== "") ? (commType === "PERCENTAGE" ? parseFloat(commValue) : Math.round(parseFloat(commValue) * 100)) : null,
        commission_reason: (partnerId !== "NONE" && commOvOpen) ? (commReason || null) : null,
      };
      if (foundCustomer) body.customer_id = foundCustomer.id;
      else body.customer_inline = { first_name: cust.first_name, last_name: cust.last_name, phone: cust.phone, email: cust.email || null };
      if (isEdit) return (await api.patch(`/api/bookings/${booking.id}`, body, { params: { organization_id: orgId } })).data;
      return (await api.post("/api/bookings", body)).data;
    },
    onSuccess: (b) => { toast.success(isEdit ? "Réservation modifiée" : `Réservation ${b.booking_reference || ""} créée`); qc.invalidateQueries(); onSaved?.(b); onOpenChange(false); },
    onError: (e) => {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.type === "conflict") { setConflicts(d.conflicts); toast.warning("Conflit de ressource"); }
      else toast.error(formatApiError(d) || e.message);
    },
  });

  const customerOk = foundCustomer || (cust.first_name && cust.last_name && cust.phone);
  const canSubmit = customerOk && formulaId && date && start;
  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" aria-describedby={undefined} className="w-full overflow-y-auto p-0 sm:max-w-xl" data-testid="booking-form-drawer">
        <SheetHeader className="border-b border-slate-100 px-6 py-4"><SheetTitle>{isEdit ? `Modifier ${booking.booking_reference}` : "Nouvelle réservation"}</SheetTitle><SheetDescription className="sr-only">Formulaire de réservation : client, formule, horaire, options et tarification.</SheetDescription></SheetHeader>
        <div className="space-y-6 p-6 pb-28">
          {/* Client inline */}
          <div>
            <p className={section}>Client</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input placeholder="Prénom" value={cust.first_name} onChange={(e) => setCust({ ...cust, first_name: e.target.value })} data-testid="booking-customer-firstname" className={input} disabled={isEdit} />
              <input placeholder="Nom" value={cust.last_name} onChange={(e) => setCust({ ...cust, last_name: e.target.value })} data-testid="booking-customer-lastname" className={input} disabled={isEdit} />
            </div>
            <div className="relative mt-2">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input placeholder="Téléphone" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} data-testid="booking-customer-phone" className={`${input} pl-9`} disabled={isEdit} />
            </div>
            {!isEdit && <input placeholder="E-mail (optionnel)" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} className={`${input} mt-2`} />}
            {foundCustomer && (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid="existing-customer-found">
                <Check className="h-4 w-4 text-emerald-600" />
                <span className="text-emerald-800">Client existant : <b>{foundCustomer.name}</b>{foundCustomer.bookings_count ? ` · ${foundCustomer.bookings_count} réservations` : ""}</span>
                {!isEdit && <button type="button" onClick={() => setFoundCustomer(null)} className="ml-auto text-xs text-slate-500 hover:text-slate-800"><X className="h-3.5 w-3.5" /></button>}
              </div>
            )}
          </div>

          {/* Activity + Formula */}
          <div>
            <p className={section}>Activité</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {activities.map((a) => (
                <button key={a.id} type="button" onClick={() => { setActivityId(a.id); setFormulaId(""); setVehicleIds([]); }} data-testid={`activity-option-${a.id}`}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${activityId === a.id ? "border-primary bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{a.name}</button>
              ))}
            </div>
          </div>
          {activityId && (
            <div>
              <p className={section}>Formule</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {formulas.map((f) => (
                  <button key={f.id} type="button" onClick={() => { setFormulaId(f.id); setVehicleIds([]); }} data-testid={`formula-option-${f.id}`}
                    className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${formulaId === f.id ? "border-primary bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                    <p className="text-sm font-semibold text-slate-900">{f.name}</p>
                    <p className="text-xs text-slate-500">{f.duration_minutes} min · {f.pricing_type === "FIXED" ? `${formatMoney(f.fixed_price, currency)} forfait` : `${formatMoney(f.unit_price, currency)}/pers`}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div><label className={label}>Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="booking-date-input" className={input} /></div>
            <div><label className={label}>Début</label><FieldSelect testId="booking-start-input" className="w-full" value={start} onChange={setStart} options={TIME_SLOTS.map((t) => ({ value: t, label: t }))} /></div>
            <div><label className={label}>Fin</label><input value={endTime} readOnly data-testid="booking-end-display" className={`${input} bg-slate-50 text-slate-500`} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Participants</label><input type="number" min="1" value={pax} onChange={(e) => setPax(e.target.value)} data-testid="booking-participants-input" className={input} /></div>
            <div><label className={label}>Source</label><FieldSelect testId="booking-source-input" className="w-full" value={source} onChange={setSource} options={Object.entries(SOURCES).map(([k, l]) => ({ value: k, label: l }))} /></div>
          </div>

          {/* Extras */}
          {formulaId && compatExtras.length > 0 && (
            <div>
              <p className={section}>Options</p>
              <div className="mt-2 space-y-2">
                {compatExtras.map((e) => {
                  const on = e.id in extras;
                  const price = e.pricing_type === "FIXED" ? `${formatMoney(e.fixed_price, currency)} forfait` : `${formatMoney(e.unit_price, currency)}/pers`;
                  return (
                    <div key={e.id} className={`flex items-center gap-3 rounded-lg border p-2.5 ${on ? "border-primary bg-emerald-50" : "border-slate-200"}`}>
                      <button type="button" onClick={() => setExtras((x) => { const n = { ...x }; if (on) delete n[e.id]; else n[e.id] = e.quantity_mode === "CUSTOM_QUANTITY" ? 1 : (e.quantity_mode === "ALL_PARTICIPANTS" ? pax : 1); return n; })} data-testid={`extra-toggle-${e.id}`}
                        className={`flex h-5 w-5 items-center justify-center rounded border ${on ? "border-primary bg-primary text-white" : "border-slate-300"}`}>{on && <Check className="h-3.5 w-3.5" />}</button>
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800">{e.name}</p><p className="text-xs text-slate-500">{price}</p></div>
                      {on && e.quantity_mode === "CUSTOM_QUANTITY" && (
                        <input type="number" min="1" max={pax} value={extras[e.id]} onChange={(ev) => setExtras((x) => ({ ...x, [e.id]: Math.max(1, Math.min(Number(pax), Number(ev.target.value) || 1)) }))} data-testid={`extra-qty-${e.id}`} className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resources */}
          <div>
            <p className={section}>Ressources <span className="font-normal normal-case text-slate-400">(optionnel)</span></p>
            <div className="mt-2 space-y-3">
              <div><p className="mb-1.5 flex items-center gap-1.5 text-xs text-slate-500"><Car className="h-3.5 w-3.5" /> Véhicules</p>
                <div className="flex flex-wrap gap-1.5">{(vehs?.vehicles || []).map((v) => (<button key={v.id} type="button" onClick={() => toggle(vehicleIds, setVehicleIds, v.id)} data-testid={`vehicle-chip-${v.code}`} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${vehicleIds.includes(v.id) ? "border-primary bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>{v.code}</button>))}
                {formulaId && (vehs?.vehicles || []).length === 0 && <p className="text-xs text-slate-400" data-testid="no-compatible-vehicle">Aucune ressource compatible disponible.</p>}</div></div>
              <div><p className="mb-1.5 flex items-center gap-1.5 text-xs text-slate-500"><User className="h-3.5 w-3.5" /> Guides</p>
                <div className="flex flex-wrap gap-1.5">{(emps?.employees || []).map((e) => (<button key={e.id} type="button" onClick={() => toggle(employeeIds, setEmployeeIds, e.id)} data-testid={`employee-chip-${e.id}`} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${employeeIds.includes(e.id) ? "border-primary bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>{e.name}</button>))}</div></div>
            </div>
          </div>

          <div><label className={label}>Notes internes</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="booking-notes-input" className={input} /></div>

          {/* Price summary + discount */}
          {summary && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="price-summary">
              <p className={section}>Récapitulatif</p>
              <div className="mt-2 space-y-1.5">
                {summary.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm"><span className="text-slate-600">{i.name} <span className="text-slate-400">{i.qty > 1 ? `${i.qty} × ${formatMoney(i.unit, currency)}` : ""}</span></span><span className="font-mono text-slate-800">{formatMoney(i.total, currency)}</span></div>
                ))}
                <div className="flex justify-between border-t border-slate-200 pt-1.5 text-sm"><span className="text-slate-500">Sous-total</span><span className="font-mono font-medium text-slate-900" data-testid="summary-subtotal">{formatMoney(summary.subtotal, currency)}</span></div>
                {summary.damount > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Réduction</span><span className="font-mono">-{formatMoney(summary.damount, currency)}</span></div>}
                <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="text-sm font-semibold text-slate-900">Total à encaisser</span><span className="font-mono text-lg font-bold text-slate-900" data-testid="summary-total">{formatMoney(summary.final, currency)}</span></div>
              </div>
              {can("bookings.discount") && (
                discOpen ? (
                  <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                    <div className="flex items-center justify-between"><span className="text-xs font-semibold text-slate-600">Réduction</span><button type="button" onClick={() => { setDiscOpen(false); setDiscValue(""); }} className="text-xs text-slate-500 hover:text-slate-800">Retirer</button></div>
                    <div className="flex gap-2">
                      <FieldSelect testId="discount-type" className="min-w-[130px]" value={discType} onChange={setDiscType} options={[{ value: "PERCENTAGE", label: "Pourcentage" }, { value: "FIXED_AMOUNT", label: "Montant fixe" }]} />
                      <input type="number" min="0" value={discValue} onChange={(e) => setDiscValue(e.target.value)} placeholder={discType === "PERCENTAGE" ? "%" : "MAD"} data-testid="discount-value" className="w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                      <input value={discReason} onChange={(e) => setDiscReason(e.target.value)} placeholder="Motif" data-testid="discount-reason" className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setDiscOpen(true)} data-testid="add-discount-button" className="mt-3 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"><Percent className="h-4 w-4" /> Ajouter une réduction</button>
                )
              )}
              {can("bookings.change_price") && (
                ovOpen ? (
                  <div className="mt-2 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-center justify-between"><span className="text-xs font-semibold text-amber-800">Prix forcé (exceptionnel)</span><button type="button" onClick={() => { setOvOpen(false); setOvVal(""); }} className="text-xs text-slate-500 hover:text-slate-800">Retirer</button></div>
                    <div className="flex gap-2">
                      <input type="number" min="0" value={ovVal} onChange={(e) => setOvVal(e.target.value)} placeholder={`Montant (${currency})`} data-testid="price-override-value" className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                      <input value={ovReason} onChange={(e) => setOvReason(e.target.value)} placeholder="Raison" data-testid="price-override-reason" className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                    </div>
                    {summary && ovVal !== "" && <p className="text-xs text-amber-700">Calculé : {formatMoney(summary.final, currency)} → forcé {formatMoney(Math.round(parseFloat(ovVal) * 100) || 0, currency)}</p>}
                  </div>
                ) : (
                  <button type="button" onClick={() => setOvOpen(true)} data-testid="add-override-button" className="mt-2 flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:underline"><Tag className="h-4 w-4" /> Forcer un prix</button>
                )
              )}
            </div>
          )}

          {can("partners.view") && (
            <div data-testid="booking-partner-section">
              <p className={section}>Partenaire <span className="font-normal normal-case text-slate-400">(optionnel)</span></p>
              <FieldSelect testId="booking-partner-select" className="mt-2 w-full" value={partnerId} onChange={setPartnerId}
                options={[{ value: "NONE", label: "Aucun partenaire" }, ...(partnersData?.partners || []).map((p) => ({ value: p.id, label: `${p.name} — ${commissionRuleText(p.default_commission_type, p.default_commission_value, currency)}` }))]} />
              {selectedPartner && commissionPreview && (
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="commission-preview">
                  <div className="flex justify-between"><span className="text-slate-500">Règle</span><span className="text-slate-700">{commOvOpen ? "Ajustée" : commissionRuleText(selectedPartner.default_commission_type, selectedPartner.default_commission_value, currency)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Commission estimée</span><span className="font-mono font-medium text-indigo-700" data-testid="commission-amount">{formatMoney(commissionPreview.amount, currency)}</span></div>
                  <div className="flex justify-between border-t border-slate-200 pt-1"><span className="text-slate-500">Net commercial estimé</span><span className="font-mono text-slate-700" data-testid="commission-net">{formatMoney(commissionPreview.net, currency)}</span></div>
                  <p className="mt-1 text-xs text-slate-400">Indicatif — le paiement client reste intégral sur place.</p>
                  {can("bookings.override_commission") && (
                    commOvOpen ? (
                      <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-2.5">
                        <div className="flex items-center justify-between"><span className="text-xs font-semibold text-slate-600">Commission ajustée</span><button type="button" onClick={() => { setCommOvOpen(false); setCommValue(""); setCommReason(""); }} className="text-xs text-slate-500 hover:text-slate-800">Rétablir la règle</button></div>
                        <div className="flex gap-2">
                          <FieldSelect testId="commission-override-type" className="min-w-[130px]" value={commType} onChange={setCommType} options={Object.entries(COMMISSION_TYPES).map(([k, v]) => ({ value: k, label: v }))} />
                          <input type="number" min="0" step="0.01" value={commValue} onChange={(e) => setCommValue(e.target.value)} placeholder={commType === "PERCENTAGE" ? "%" : currency} data-testid="commission-override-value" className="w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                          <input value={commReason} onChange={(e) => setCommReason(e.target.value)} placeholder="Motif" data-testid="commission-override-reason" className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                        </div>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setCommOvOpen(true)} data-testid="add-commission-override-button" className="mt-2 flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:underline"><Handshake className="h-4 w-4" /> Ajuster la commission</button>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="conflict-warning">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Conflit de ressource</p>
              {conflicts.map((c, i) => <p key={i} className="mt-1 text-xs text-amber-700">{(c.labels || []).join(", ")} déjà affecté à {c.booking_reference} ({c.start_time}–{c.end_time}).</p>)}
              {can("bookings.override_conflict") && <button type="button" onClick={() => save.mutate(true)} data-testid="override-conflict-button" className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">Forcer malgré le conflit</button>}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 border-t border-slate-100 bg-white px-6 py-4">
          <button disabled={!canSubmit || save.isPending} onClick={() => save.mutate(false)} data-testid="submit-booking-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Enregistrer" : "Créer la réservation"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
